interface StoredEntry<T> {
  value: T;
  expiresAt: number | null;
}

// Web Storage wrapper with optional TTL. All access is wrapped in try/catch so
// disabled storage (private mode, quota) never throws. The backing store is
// read lazily so simply importing this module never touches storage.
const createStorageUtils = (getStorage: () => Storage) => ({
  set<T>(key: string, value: T, ttlMs?: number): void {
    const entry: StoredEntry<T> = {
      value,
      expiresAt: ttlMs ? Date.now() + ttlMs : null,
    };

    try {
      getStorage().setItem(key, JSON.stringify(entry));
    } catch {
      // storage unavailable — skip silently
    }
  },

  get<T>(key: string): T | null {
    try {
      const raw = getStorage().getItem(key);
      if (!raw) {
        return null;
      }

      const entry = JSON.parse(raw) as StoredEntry<T>;
      if (entry.expiresAt !== null && Date.now() > entry.expiresAt) {
        getStorage().removeItem(key);
        return null;
      }

      return entry.value;
    } catch {
      this.remove(key);
      return null;
    }
  },

  remove(key: string): void {
    try {
      getStorage().removeItem(key);
    } catch {
      // storage unavailable — nothing to clear
    }
  },
});

export const storageUtils = createStorageUtils(() => localStorage);

export const sessionStorageUtils = createStorageUtils(() => sessionStorage);
